import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { useApi, useDashboard, useSessionStorage } from '@/hooks'
import { Coordinate, boundsFromAllPoints, getResponsiveUSZoom, getUSBounds, zoomToCenter } from '@/lib/map/geo'
import type { MapStyle } from '@/lib/map/MapStyleToggles'
import type { SavedView } from '@/types/dashboard-types'

import { DEFAULT_OPTIONAL_COLUMN_IDS, normalizeColumns } from './trackerColumnConfig'
import {
  RowStatus,
  ShipmentFilterValue,
  StatusFilterValue,
  TrackerHistory,
  TrackerHistoryPoint,
  TrackerShipmentRow,
  TrackerState,
} from './types'
import {
  correlateLocationTemps,
  getRowStatus,
  getTrackerStatus,
  rowId,
  rowKey,
  trackerSearchBlob,
  tsToMillis,
} from './utils'

export type LayoutMode = 'map' | 'table' | 'split'
export type { MapStyle }

export interface SidebarState {
  sidebarWidth: number
  limitToMap: boolean
  layoutMode: LayoutMode
  splitRatio: number
  listPanelWidth: number
}

export type GeofenceFilterValue = 'all' | 'inside' | 'outside'
export interface TrackerFilters {
  geofence: GeofenceFilterValue
  shipment: ShipmentFilterValue
  status: StatusFilterValue
}
const defaultFilters: TrackerFilters = { geofence: 'all', shipment: 'active', status: 'all' }

export interface MapState {
  bounds: { north: number; south: number; east: number; west: number }
  pitch: number
  bearing: number
  zoom: number
  focus: boolean
  showQuickView: boolean
  style: MapStyle
  clustersEnabled: boolean
  trackerLabels: boolean
}

interface TrackerMapContextType {
  companyId?: number
  setCompanyId: (id: number | undefined) => void
  searchTerm: string
  setSearchTerm: (term: string) => void
  filters: TrackerFilters
  setFilters: (f: TrackerFilters) => void
  setListPanelWidth: (width: number) => void
  visibleColumns: string[]
  setVisibleColumns: (cols: string[]) => void
  allTrackers: TrackerShipmentRow[]
  filteredTrackers: TrackerShipmentRow[]
  viewportFilteredTrackers: TrackerShipmentRow[]
  geojsonData: GeoJSON.GeoJSON
  currentRowId?: number
  setCurrentRowId: (id: number | undefined) => void
  currentRow?: TrackerShipmentRow
  currentTrackerData?: TrackerState
  animatedTrackerLocation?: Coordinate
  showTrackerModal: boolean
  setShowTrackerModal: (show: boolean) => void
  // Shipment location-history mode (transient, not persisted).
  historyMode: boolean
  setHistoryMode: (on: boolean) => void
  historyPoints?: TrackerHistoryPoint[]
  historyLoading: boolean
  // Temperature thresholds (Celsius). low = blue-below boundary (shipment min),
  // high = red-above boundary (shipment max); either may be null when that end
  // isn't defined. In range → green.
  historyLowC: number | null
  setHistoryLowC: (c: number | null) => void
  historyHighC: number | null
  setHistoryHighC: (c: number | null) => void
  historyRangeC?: { min: number; max: number }
  historySelectedIndex?: number
  setHistorySelectedIndex: (i: number | undefined) => void
  sidebarState: SidebarState
  setSidebarState: React.Dispatch<React.SetStateAction<SidebarState>>
  mapState: MapState
  setMapState: React.Dispatch<React.SetStateAction<MapState>>
  isLoading: boolean
  error: string
  updatedAt?: Date
  reset: () => void
  isReady: boolean
  setIsReady: (ready: boolean) => void
  resetCount: number
  /** The saved view the page is showing, or null. */
  selectedView: TrackerSavedView | null
  setSelectedView: (view: TrackerSavedView | null) => void
  /** Switches the page to `view` and selects it. */
  applyView: (view: TrackerSavedView) => void
  /** True while a view is being applied. */
  isSwitchingView: boolean
  /** The page as it is now, as a view called `name`. */
  currentView: (name: string, isDefault: boolean) => TrackerSavedView
  /** Which parts of the page this tab restored from session storage, so a default view doesn't override them. */
  hydrated: { companyId: boolean; sidebarState: boolean; mapState: boolean; filters: boolean; columns: boolean }
  detailSheetIsOpen: boolean
  setDetailSheetIsOpen: (open: boolean) => void
  /** Shows the selected row's details: the sheet beside the list, or the modal on phones. */
  openDetails: () => void
}

/** A saved tracker map view: everything about the page but the selection and search. */
export interface TrackerSavedView extends SavedView {
  companyId?: number
  filters: TrackerFilters
  sidebarState: SidebarState
  mapState: MapState
  columns: string[]
}

const TrackerMapContext = createContext<TrackerMapContextType | undefined>(undefined)

const defaultMapState: MapState = {
  pitch: 30,
  bearing: 0,
  zoom: getResponsiveUSZoom(),
  style: 'road',
  bounds: getUSBounds(6),
  clustersEnabled: true,
  trackerLabels: false,
  focus: false,
  showQuickView: true,
}

export const DEFAULT_LIST_PANEL_WIDTH = 350

export const defaultSidebarState: SidebarState = {
  limitToMap: false,
  sidebarWidth: 400,
  layoutMode: 'map',
  splitRatio: 0.5,
  listPanelWidth: DEFAULT_LIST_PANEL_WIDTH,
}

const SESSION_KEYS = {
  sidebarState: 'trackerMap.sidebarState',
  mapState: 'trackerMap.mapState',
  searchTerm: 'trackerMap.searchTerm',
  filters: 'trackerMap.filters',
  columns: 'trackerMap.columns',
  companyId: 'trackerMap.companyId',
} as const

/**
 * The company last picked on the tracker map, read straight from this tab's
 * cache so other pages (the new-shipment form) can default to it without
 * mounting the provider.
 */
export const cachedTrackerMapCompanyId = (): number | undefined => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEYS.companyId)
    const parsed: unknown = raw === null ? undefined : JSON.parse(raw)
    return typeof parsed === 'number' ? parsed : undefined
  } catch {
    return undefined
  }
}

const defaultVisibleColumns: string[] = [...DEFAULT_OPTIONAL_COLUMN_IDS]

const fullTrackerCache = new Map<string, TrackerState & { cacheTime: Date }>()

const statusValue = (status: RowStatus): number =>
  status === 'stale' || status === 'completed' ? -1 : status === 'warning' ? 1 : 0

export const TrackerMapProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { api } = useApi()
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [allTrackers, setAllTrackers] = useState<TrackerShipmentRow[]>([])
  const [currentRowId, _setCurrentRowId] = useState<number | undefined>()
  const [currentTrackerData, setCurrentTrackerData] = useState<TrackerState | undefined>()
  const [animatedTrackerLocation, setAnimatedTrackerLocation] = useState<Coordinate | undefined>()
  const [showTrackerModal, setShowTrackerModal] = useState(false)
  const [detailSheetIsOpen, setDetailSheetIsOpen] = useState(false)
  const [selectedView, setSelectedView] = useState<TrackerSavedView | null>(null)
  const [isSwitchingView, setIsSwitchingView] = useState(false)
  // Set while applying a view, so loading its company keeps the view's framing instead of
  // zooming to fit the company's trackers.
  const keepFramingRef = useRef(false)
  const [updatedAt, setUpdatedAt] = useState<Date>()
  const [resetCount, setResetCount] = useState(0)

  // Shipment location-history mode (transient — never persisted to sessionStorage).
  const [historyMode, setHistoryMode] = useState(false)
  const [historyPoints, setHistoryPoints] = useState<TrackerHistoryPoint[] | undefined>()
  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyLowC, setHistoryLowC] = useState<number | null>(null)
  const [historyHighC, setHistoryHighC] = useState<number | null>(null)
  const [historyRangeC, setHistoryRangeC] = useState<{ min: number; max: number } | undefined>()
  const [historySelectedIndex, setHistorySelectedIndex] = useState<number | undefined>()

  const { configuration } = useDashboard()
  const [companyId, setCompanyId, companyHydrated] = useSessionStorage<number | undefined>(
    SESSION_KEYS.companyId,
    undefined
  )
  const [sidebarState, setSidebarState, sidebarHydrated] = useSessionStorage<SidebarState>(
    SESSION_KEYS.sidebarState,
    defaultSidebarState
  )
  const [mapState, setMapState, mapHydrated] = useSessionStorage<MapState>(SESSION_KEYS.mapState, defaultMapState)
  const [searchTerm, setSearchTerm] = useSessionStorage<string>(SESSION_KEYS.searchTerm, '')
  const [storedFilters, setFilters, filtersHydrated] = useSessionStorage<TrackerFilters>(
    SESSION_KEYS.filters,
    defaultFilters
  )
  // Stored filters may predate the shipment/status keys — merge over the defaults.
  const filters = useMemo(() => ({ ...defaultFilters, ...storedFilters }), [storedFilters])
  const [storedColumns, setVisibleColumnsState, columnsHydrated] = useSessionStorage<string[]>(
    SESSION_KEYS.columns,
    defaultVisibleColumns
  )
  // Session-stored columns may predate the shipment-centric column set too.
  const visibleColumns = useMemo(() => normalizeColumns(storedColumns), [storedColumns])
  const [updatingListWidth, setUpdatingListWidth] = useState(0)
  const [updatingColumns, setUpdatingColumns] = useState<string[] | null>(null)

  // Seed the list-panel width from the user's saved profile value (unless the tab already has a
  // session value), mirroring the asset map's sidebar-width behaviour.
  useEffect(() => {
    if (sidebarHydrated || configuration.trackerMapListWidth == null) return
    setSidebarState((prev) => ({ ...prev, listPanelWidth: configuration.trackerMapListWidth as number }))
  }, [configuration.trackerMapListWidth, sidebarHydrated, setSidebarState])

  // Update the list-panel width and remember it for a debounced profile save.
  const setListPanelWidth = useCallback(
    (width: number) => {
      setSidebarState((prev) => ({ ...prev, listPanelWidth: width }))
      setUpdatingListWidth(width)
    },
    [setSidebarState]
  )

  // Debounce persisting the list-panel width to the user's profile while dragging.
  useEffect(() => {
    if (!api || updatingListWidth === 0) return
    const timeout = setTimeout(() => {
      api
        .put({
          path: `app/profiles/tracker-list-width`,
          origin: 'TrackerMap',
          body: { trackerMapListWidth: Math.round(updatingListWidth) },
        })
        .catch((e) => console.error('Failed to save tracker list width', e))
    }, 500)
    return () => clearTimeout(timeout)
  }, [updatingListWidth, api])

  // Seed the visible/ordered columns from the user's saved profile (unless the tab already has
  // one). Saved profiles may predate the shipment-centric column set — normalize them.
  useEffect(() => {
    if (columnsHydrated || configuration.trackerMapColumns == null) return
    setVisibleColumnsState(normalizeColumns(configuration.trackerMapColumns as string[]))
  }, [configuration.trackerMapColumns, columnsHydrated, setVisibleColumnsState])

  // Update the visible/ordered columns and remember them for a debounced profile save.
  const setVisibleColumns = useCallback(
    (cols: string[]) => {
      setVisibleColumnsState(cols)
      setUpdatingColumns(cols)
    },
    [setVisibleColumnsState]
  )

  // Debounce persisting the column selection/order to the user's profile.
  useEffect(() => {
    if (!api || updatingColumns == null) return
    const timeout = setTimeout(() => {
      api
        .put({
          path: `app/profiles/tracker-columns`,
          origin: 'TrackerMap',
          body: { trackerMapColumns: updatingColumns },
        })
        .catch((e) => console.error('Failed to save tracker columns', e))
    }, 500)
    return () => clearTimeout(timeout)
  }, [updatingColumns, api])

  const setCurrentRowId = useCallback(
    (id: number | undefined) => {
      _setCurrentRowId(id)
      if (id !== undefined) setMapState((prev) => ({ ...prev, showQuickView: true }))
      // Nothing selected, nothing to show: the sheet goes with the selection, as on the asset map.
      else setDetailSheetIsOpen(false)
    },
    [setMapState]
  )

  // Load shipment/tracker rows for the selected company + shipment filter; refresh every 60s.
  useEffect(() => {
    if (!isReady) return
    if (!companyId) {
      setAllTrackers([])
      setIsLoading(false)
      return
    }

    setAllTrackers([])
    setCurrentRowId(undefined)
    setCurrentTrackerData(undefined)
    setAnimatedTrackerLocation(undefined)
    setIsLoading(true)
    setError('')

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const fetchTrackers = async (zoom: boolean) => {
      if (!api) return
      const path = `/trackers/state/summary?companyId=${companyId}&filter=${filters.shipment}`
      const response = await api.get<TrackerShipmentRow[]>({ path, origin: 'TrackerMap' })
      if (cancelled) return

      if (response.error) {
        setError(response.message ?? 'An unexpected error occurred')
        setAllTrackers([])
      } else if (response.data?.length) {
        setUpdatedAt(new Date())
        setAllTrackers(response.data)
        setError('')
        if (zoom && keepFramingRef.current) {
          keepFramingRef.current = false
        } else if (zoom) {
          const points = response.data
            .filter((t) => t.location)
            .map((t) => ({ latitude: t.location!.latitude, longitude: t.location!.longitude }))
          if (points.length) {
            const { bounds, zoom: z } = zoomToCenter(points, 0, 15)
            setMapState((prev) => ({ ...prev, bounds, zoom: z }))
          }
        }
      } else {
        setAllTrackers([])
        setError('No trackers found')
      }
      setIsLoading(false)
      if (!cancelled) timeoutId = setTimeout(() => fetchTrackers(false), 60 * 1000)
    }

    // Only re-zoom on a company change, not when the shipment filter changes.
    fetchTrackers(true)
    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [api, companyId, filters.shipment, isReady, setCurrentRowId, setMapState])

  const currentRow = useMemo(
    () => (currentRowId != null ? allTrackers.find((t) => rowId(t) === currentRowId) : undefined),
    [allTrackers, currentRowId]
  )

  // Fetch full tracker detail when a row is selected. Rows without a tracker
  // (pre-created shipments) have nothing to fetch — the quick view/modal render
  // from the summary row.
  useEffect(() => {
    if (!currentRow || currentRow.trackerId == null) {
      setCurrentTrackerData(undefined)
      setAnimatedTrackerLocation(undefined)
      return
    }
    const trackerId = currentRow.trackerId
    const shipmentId = currentRow.shipmentId

    if (currentRow.location) {
      setAnimatedTrackerLocation({
        latitude: currentRow.location.latitude,
        longitude: currentRow.location.longitude,
      })
    }

    const cacheKey = `${trackerId}:${shipmentId ?? ''}`
    const cached = fullTrackerCache.get(cacheKey)
    if (cached && cached.cacheTime > new Date(Date.now() - 60 * 1000)) {
      setCurrentTrackerData(cached)
    }

    let cancelled = false
    const fetchDetail = async () => {
      try {
        const data = await api.simpleGet<TrackerState>({
          path: `trackers/state/${trackerId}${shipmentId != null ? `?shipmentId=${shipmentId}` : ''}`,
          origin: 'TrackerMap',
        })
        if (cancelled) return
        fullTrackerCache.set(cacheKey, { ...data, cacheTime: new Date() })
        if (data.location?.latitude != null && data.location?.longitude != null) {
          setAnimatedTrackerLocation({ latitude: data.location.latitude, longitude: data.location.longitude })
        }
        setCurrentTrackerData(data)
      } catch (ex) {
        console.error(ex)
        fullTrackerCache.delete(cacheKey)
        setError('Could not load tracker')
        setCurrentTrackerData(undefined)
        setShowTrackerModal(false)
      }
    }
    fetchDetail()
    return () => {
      cancelled = true
    }
  }, [api, currentRow])

  // Turn history mode off whenever the selection clears (nothing to trace).
  useEffect(() => {
    if (currentRowId === undefined) setHistoryMode(false)
  }, [currentRowId])

  // Primitives that identify the tracker + shipment window for history. Keyed on
  // primitives so the 60s summary refresh (new row objects, same values) doesn't
  // re-fetch or reset the user's slider position.
  const histTrackerId = currentRow?.trackerId ?? null
  const histShipmentId = currentRow?.shipmentId ?? null
  const histStartMs = tsToMillis(currentRow?.actualStartAt)
  const histEndMs = tsToMillis(currentRow?.actualEndAt)
  const histMinC = currentRow?.shipmentMinTemperatureCelsius ?? null
  const histMaxC = currentRow?.shipmentMaxTemperatureCelsius ?? null

  // Load the selected tracker's location trail (with correlated temperatures) for
  // the shipment window when history mode is on.
  useEffect(() => {
    if (!historyMode || !api || histTrackerId == null) {
      setHistoryPoints(undefined)
      setHistoryRangeC(undefined)
      setHistorySelectedIndex(undefined)
      return
    }
    const endMs = histEndMs ?? Date.now()
    // No shipment start (unassigned tracker) → trail the last 7 days.
    const startMs = histStartMs ?? endMs - 7 * 24 * 60 * 60 * 1000
    const fromISO = new Date(startMs).toISOString()
    const toISO = new Date(endMs).toISOString()

    let cancelled = false
    setHistoryLoading(true)
    setHistorySelectedIndex(undefined)
    const fetchHistory = async () => {
      try {
        const data = await api.simpleGet<TrackerHistory>({
          path: `trackers/history/${histTrackerId}?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`,
          origin: 'TrackerMap',
        })
        if (cancelled) return
        const points = correlateLocationTemps(data.location, data.temperature)
        setHistoryPoints(points)
        const temps = points.map((p) => p.temperatureCelsius).filter((v): v is number => v != null)
        if (temps.length) {
          const min = Math.min(...temps)
          const max = Math.max(...temps)
          setHistoryRangeC({ min, max })
          const clamp = (v: number) => Math.min(Math.max(v, min), max)
          // Default the boundaries from the shipment's allowed range: low (blue
          // below) = shipment min, high (red above) = shipment max. When neither
          // is defined, fall back to a single midpoint high threshold (green/red).
          if (histMinC != null || histMaxC != null) {
            setHistoryLowC(histMinC != null ? clamp(histMinC) : null)
            setHistoryHighC(histMaxC != null ? clamp(histMaxC) : null)
          } else {
            setHistoryLowC(null)
            setHistoryHighC(clamp((min + max) / 2))
          }
        } else {
          setHistoryRangeC(undefined)
        }
      } catch (ex) {
        if (!cancelled) {
          console.error(ex)
          setHistoryPoints([])
          setHistoryRangeC(undefined)
        }
      } finally {
        if (!cancelled) setHistoryLoading(false)
      }
    }
    fetchHistory()
    return () => {
      cancelled = true
    }
  }, [api, historyMode, histTrackerId, histShipmentId, histStartMs, histEndMs, histMinC, histMaxC])

  const searchIndex = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of allTrackers) map.set(rowKey(t), trackerSearchBlob(t))
    return map
  }, [allTrackers])

  const filteredTrackers = useMemo(() => {
    let rows = allTrackers
    if (filters.status !== 'all') {
      // Online/offline only applies to live tracker rows; planned (no tracker)
      // and completed (frozen) rows are neither.
      rows = rows.filter((t) => {
        if (t.trackerId == null || t.asOf === 'shipmentEnd') return false
        const stale = getTrackerStatus(t) === 'stale'
        return filters.status === 'offline' ? stale : !stale
      })
    }
    if (!searchTerm) return rows
    const needle = searchTerm.toLowerCase()
    return rows.filter((t) => searchIndex.get(rowKey(t))?.includes(needle))
  }, [allTrackers, filters.status, searchTerm, searchIndex, resetCount])

  const viewportFilteredTrackers = useMemo(() => {
    if (!sidebarState.limitToMap || !mapState.bounds || sidebarState.layoutMode === 'table') {
      return filteredTrackers
    }
    return filteredTrackers.filter((t) => {
      if (!t.location) return false
      const { latitude, longitude } = t.location
      return (
        latitude <= mapState.bounds.north &&
        latitude >= mapState.bounds.south &&
        longitude <= mapState.bounds.east &&
        longitude >= mapState.bounds.west
      )
    })
  }, [filteredTrackers, mapState.bounds, sidebarState])

  const geojsonData = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: viewportFilteredTrackers
        .filter((t) => t.location)
        .map((t) => {
          const status = getRowStatus(t)
          const id = rowId(t)
          const useDetailLocation =
            id === currentRowId &&
            currentTrackerData?.id === t.trackerId &&
            currentTrackerData.location?.longitude != null
          return {
            type: 'Feature' as const,
            id,
            geometry: {
              type: 'Point' as const,
              coordinates: useDetailLocation
                ? [currentTrackerData!.location!.longitude!, currentTrackerData!.location!.latitude as number]
                : [t.location!.longitude, t.location!.latitude],
            },
            properties: {
              id,
              trackerId: t.trackerId,
              shipmentId: t.shipmentId,
              label: t.shipmentName ?? t.label ?? '',
              status,
              statusValue: statusValue(status),
              selected: id === currentRowId,
            },
          }
        }),
    }),
    [viewportFilteredTrackers, currentRowId, currentTrackerData]
  )

  const reset = () => {
    Object.values(SESSION_KEYS).forEach((key) => {
      if (key !== SESSION_KEYS.companyId) sessionStorage.removeItem(key)
    })
    const points = allTrackers
      .filter((t) => t.location)
      .map((t) => ({ latitude: t.location!.latitude, longitude: t.location!.longitude }))
    if (points.length) {
      const { bounds, zoom } = boundsFromAllPoints(points)
      setMapState({ ...defaultMapState, bounds, zoom })
    } else {
      setMapState(defaultMapState)
    }
    setSidebarState(defaultSidebarState)
    setSearchTerm('')
    setFilters(defaultFilters)
    setListPanelWidth(DEFAULT_LIST_PANEL_WIDTH)
    setVisibleColumns([...defaultVisibleColumns])
    setCurrentRowId(undefined)
    setCurrentTrackerData(undefined)
    setAnimatedTrackerLocation(undefined)
    setShowTrackerModal(false)
    setDetailSheetIsOpen(false)
    setHistoryMode(false)
    setSelectedView(null)
    setResetCount((c) => c + 1)
  }

  const openDetails = () => {
    // Phones have no room for the sheet, so they still get the modal.
    if (window.matchMedia('(min-width: 768px)').matches) setDetailSheetIsOpen(true)
    else setShowTrackerModal(true)
  }

  const currentView = (name: string, isDefault: boolean): TrackerSavedView => ({
    name,
    isDefault,
    companyId,
    filters,
    sidebarState,
    mapState,
    columns: visibleColumns,
  })

  const applyView = (view: TrackerSavedView) => {
    setIsSwitchingView(true)
    setSelectedView(view)
    if (view.companyId !== companyId) keepFramingRef.current = true
    setCompanyId(view.companyId)
    setFilters({ ...defaultFilters, ...view.filters })
    setSidebarState({ ...defaultSidebarState, ...view.sidebarState })
    setMapState({ ...defaultMapState, ...view.mapState })
    // The view's columns, for this tab only: picking a view doesn't change the user's own column choice.
    setVisibleColumnsState(normalizeColumns(view.columns))
    setCurrentRowId(undefined)
    // Remounts the map at the view's framing.
    setResetCount((c) => c + 1)
    setTimeout(() => setIsSwitchingView(false), 500)
  }

  const value = useMemo(
    () => ({
      companyId,
      setCompanyId,
      searchTerm,
      setSearchTerm,
      filters,
      setFilters,
      setListPanelWidth,
      visibleColumns,
      setVisibleColumns,
      allTrackers,
      filteredTrackers,
      viewportFilteredTrackers,
      geojsonData,
      currentRowId,
      setCurrentRowId,
      currentRow,
      currentTrackerData,
      animatedTrackerLocation,
      showTrackerModal,
      setShowTrackerModal,
      historyMode,
      setHistoryMode,
      historyPoints,
      historyLoading,
      historyLowC,
      setHistoryLowC,
      historyHighC,
      setHistoryHighC,
      historyRangeC,
      historySelectedIndex,
      setHistorySelectedIndex,
      sidebarState,
      setSidebarState,
      mapState,
      setMapState,
      isLoading,
      error,
      updatedAt,
      reset,
      isReady,
      setIsReady,
      resetCount,
      selectedView,
      setSelectedView,
      applyView,
      isSwitchingView,
      currentView,
      hydrated: {
        companyId: companyHydrated,
        sidebarState: sidebarHydrated,
        mapState: mapHydrated,
        filters: filtersHydrated,
        columns: columnsHydrated,
      },
      detailSheetIsOpen,
      setDetailSheetIsOpen,
      openDetails,
    }),
    [
      companyId,
      searchTerm,
      filters,
      setFilters,
      setListPanelWidth,
      visibleColumns,
      setVisibleColumns,
      allTrackers,
      filteredTrackers,
      viewportFilteredTrackers,
      geojsonData,
      currentRowId,
      setCurrentRowId,
      currentRow,
      currentTrackerData,
      animatedTrackerLocation,
      showTrackerModal,
      historyMode,
      historyPoints,
      historyLoading,
      historyLowC,
      historyHighC,
      historyRangeC,
      historySelectedIndex,
      sidebarState,
      mapState,
      isLoading,
      error,
      updatedAt,
      isReady,
      resetCount,
      selectedView,
      isSwitchingView,
      companyHydrated,
      sidebarHydrated,
      mapHydrated,
      filtersHydrated,
      columnsHydrated,
      detailSheetIsOpen,
    ]
  )

  return <TrackerMapContext.Provider value={value}>{children}</TrackerMapContext.Provider>
}

export const useTrackerMapContext = () => {
  const ctx = useContext(TrackerMapContext)
  if (!ctx) throw new Error('useTrackerMapContext must be used within TrackerMapProvider')
  return ctx
}
